<template>
  <div>
    <v-layout v-if="noProductSettings && !loadingProductSettings">
      <i>{{ $t('pricing.penaltyFunctions.noProductSettings') }}</i>
    </v-layout>
    <v-simple-table
      v-if="!loadingProductSettings && !noProductSettings"
      dense
      class="penalty-table"
    >
      <template v-slot:default>
        <thead>
          <tr class="header">
            <th class="text-left">
              <h4>{{ $t('pricing.penaltyFunctions.ruleName') }}</h4>
            </th>
            <th class="text-left">
              <h4>{{ $t('pricing.penaltyFunctions.penaltyType') }}</h4>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(penalty, ix) in ruleNamesWithPenaltyTypes"
            :key="`${penalty.ruleName}-${ix}`"
            class="penalty-row"
          >
            <td class="pt-1 pb-1">
              <span>{{
                showIfDifferent(penalty.ruleName, 'ruleName', ix, ruleNamesWithPenaltyTypes)
              }}</span>
            </td>
            <td class="pt-1 pb-1">
              <span :class="getClassForPenaltyType(penalty.penaltyType)">{{
                $t(`pricing.penaltyFunctions.${penalty.penaltyType}`)
              }}</span>
            </td>
            <td class="pt-1 pb-1 text-right">
              <v-btn icon x-small class="view-edit-btn" @click="goToPenaltyRule(penalty)">
                <v-icon>$vuetify.icons.viewEdit</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { get, sortBy, isEmpty } from 'lodash';
import penaltyTypes from '@enums/penalty-types';
import penaltyCategories from '@enums/penalty-categories';
import { displayStoreGroupPenalty, displayMinMaxPenalty } from '@enums/feature-flags';
import showIfDifferentMixin from '../mixins/showIfDifferent';
import featureFlagsMixin from '../mixins/featureFlags';

export default {
  mixins: [showIfDifferentMixin, featureFlagsMixin],

  props: {
    scenarioKey: {
      required: true,
    },
    toolStoreGroupKey: {
      required: true,
    },
  },

  computed: {
    ...mapState('gridView', ['loadingProductSettings', 'expandedProductSettings']),
    isStoreGroupPenaltyVisible() {
      return this.isFeatureFlagEnabled(displayStoreGroupPenalty);
    },
    isMinMaxPenaltyVisible() {
      return this.isFeatureFlagEnabled(displayMinMaxPenalty);
    },
    ruleNamesWithPenaltyTypes() {
      const ruleNamesWithPenaltyTypesMap = [];
      const seenRuleKeys = new Set();
      Object.values(penaltyTypes).forEach(type => {
        if (type === penaltyTypes.storeGroupPenalty && !this.isStoreGroupPenaltyVisible) return;
        if (type === penaltyTypes.minMaxPenalty && !this.isMinMaxPenaltyVisible) return;

        // Handle penalty types with multiple penalties array
        if (
          [
            penaltyTypes.competitorPenalty,
            penaltyTypes.storeGroupPenalty,
            penaltyTypes.minMaxPenalty,
          ].includes(type)
        ) {
          const penalties = get(this.expandedProductSettings, [type, 'penalties']);
          if (penalties && Array.isArray(penalties)) {
            penalties.forEach(penalty => {
              const isActive = get(penalty, 'isActive', false);
              if (!isActive) return;
              const name = get(penalty, 'ruleDescription');
              if (name !== undefined && (name || name === '')) {
                const ruleKey = `${type}-${name}`;
                if (seenRuleKeys.has(ruleKey)) return;
                seenRuleKeys.add(ruleKey);
                ruleNamesWithPenaltyTypesMap.push({ ruleName: name, penaltyType: type });
              }
            });
          }
        } else {
          const name = get(this.expandedProductSettings, [type, 'ruleDescription']);
          if (name !== undefined && (name || name === '')) {
            ruleNamesWithPenaltyTypesMap.push({ ruleName: name, penaltyType: type });
          }
        }
      });
      return sortBy(ruleNamesWithPenaltyTypesMap, ['penaltyType', 'ruleName']);
    },

    noProductSettings() {
      return isEmpty(this.expandedProductSettings) || isEmpty(this.ruleNamesWithPenaltyTypes);
    },
  },

  methods: {
    getClassForPenaltyType(type) {
      if (type === penaltyTypes.competitorPenalty) return 'competitor';
      if (type === penaltyTypes.economicPenalty) return 'economic';
      if (type === penaltyTypes.architecturePenalty) return 'architecture';
      if (type === penaltyTypes.minMaxPenalty) return 'minmax';
      if (type === penaltyTypes.storeGroupPenalty) return 'store-group';
      return 'price-pointing';
    },

    /** Emits an event to navigate to the penalties settings page for a specific rule. */
    goToPenaltyRule({ ruleName, penaltyType }) {
      const penaltyTypeToCategory = {
        [penaltyTypes.economicPenalty]: penaltyCategories.economic,
        [penaltyTypes.competitorPenalty]: penaltyCategories.competitor,
        [penaltyTypes.architecturePenalty]: penaltyCategories.architecture,
        [penaltyTypes.minMaxPenalty]: penaltyCategories.minMax,
        [penaltyTypes.storeGroupPenalty]: penaltyCategories.storeGroup,
        [penaltyTypes.pricePointing]: penaltyCategories.pricePointing,
      };
      this.$emit('goToPenaltyRule', {
        ruleName,
        penaltyCategory: penaltyTypeToCategory[penaltyType] || null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.penalty-table {
  overflow: scroll !important;
}

.header {
  > th {
    color: black !important;
    font-size: 1.2rem !important;
  }
}

.penalty-row {
  font-size: 1.2rem !important;
  > td {
    border-bottom: 0.1rem solid $table-border-color !important;
    > span {
      font-size: 1.2rem !important;
    }
  }
}

.view-edit-btn {
  color: $common-dialog-header-color !important;
}

.economic {
  color: $pricing-economy-colour;
}

.architecture {
  color: $pricing-architecture-colour;
}

.minmax {
  color: $minMaxTrafficLightColor;
}

.competitor {
  color: $pricing-competition-colour;
}

.store-group {
  color: $storeGroupTrafficLightColor;
}

.price-pointing {
  color: $pricePointingTrafficLightColor;
}
</style>
